import { Link } from "@tanstack/react-router";
import { Hydrate } from "@tanstack/react-start";
import { visible } from "@tanstack/react-start/hydration";

/**
 * Hydrate a section shortly before it is scrolled to, not at load.
 *
 * `visible()` defaults to a 600px root margin, which on a phone puts the first
 * section below the hero *just* inside the trigger zone — so it started
 * hydrating while the document was still hydrating, and that race surfaced
 * intermittently as React #422 (boundary failed to hydrate, fell back to client
 * rendering). 200px keeps the content ready before it is read without
 * overlapping the initial hydration pass.
 */
const onApproach = () => visible({ rootMargin: "200px" });

import {
  Bot,
  ChevronRight,
  FileText,
  LineChart,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { clientGuides } from "@/components/agents/client-guides";
import { PLATFORM_CATALOG, platformInReview } from "@/lib/platforms";
import { AI_AGENT_EXAMPLES, CLAUDE_DIRECTORY_URL } from "./ai-agents-data";
import { ANNOUNCEMENT_COPY } from "./announcement-copy";
import { BrandMark } from "./brand-mark";
import {
  ArrowLink,
  EyebrowLabel,
  FeatureCheckItem,
  SectionHeading,
} from "./cards";
import { ChannelBadge } from "./channel-badge";
import { HOME_GUIDE_LINKS } from "./cross-links";
import { LandingFaq } from "./landing-faq";
import { LandingHero } from "./landing-hero";
import { LandingPricing } from "./landing-pricing";
import { LandingShowcase } from "./landing-showcase";
import { LandingSteps } from "./landing-steps";
import { MarketingCta } from "./marketing-cta";
import { MarketingStyles } from "./marketing-styles";
import { RelatedLinks } from "./related-links";
import { SiteFooter } from "./site-footer";
import { SiteHeader } from "./site-header";

/* --------------------------------------------------------- announcement */

function AnnouncementBar() {
  return (
    <div className="border-[oklch(0.9_0.03_264)] border-b bg-[oklch(0.965_0.02_264)]">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-[11px] px-6 py-[9px] text-[13.5px]">
        <span className="pd-brand-grad inline-flex h-5 items-center rounded-full px-2 font-bold text-[10.5px] text-white tracking-[0.04em]">
          NEW
        </span>
        <span className="text-[oklch(0.42_0.06_264)]">{ANNOUNCEMENT_COPY}</span>
        <Link
          to="/integrations"
          className="inline-flex items-center gap-0.5 font-semibold text-primary no-underline"
        >
          See all networks
          <ChevronRight className="size-3.5" strokeWidth={2.4} />
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- channel showcase */

function ChannelShowcase() {
  return (
    <section className="border-border border-t bg-[oklch(0.975_0.006_264)] pt-[80px] pb-[80px]">
      <div className="mx-auto max-w-[1200px] px-6">
        <SectionHeading
          eyebrow="CONNECT EVERYTHING"
          title="One composer. Every network."
          subtitle="Write once, tailor per platform, and publish everywhere from a single place. PostDaily speaks every network's language — including the character limits, media rules and previews."
        />
        <div className="mx-auto mt-11 flex max-w-[960px] flex-wrap justify-center gap-3.5">
          {PLATFORM_CATALOG.map((channel) => (
            <div
              key={channel.id}
              className="flex w-[222px] items-center gap-3 rounded-[15px] border border-border bg-card p-[14px_16px] shadow-[0_1px_2px_oklch(0.4_0.05_264/0.04)] transition-all hover:-translate-y-[3px] hover:border-[oklch(0.86_0.03_264)] hover:shadow-[0_14px_30px_-14px_oklch(0.4_0.08_264/0.24)]"
            >
              <BrandMark
                platform={channel.id}
                className="size-[38px] rounded-[11px]"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="font-semibold text-[14.5px] text-foreground">
                  {channel.label}
                </span>
                <span className="text-[11.5px] text-muted-foreground">
                  {channel.tagline}
                </span>
              </span>
              <ChannelBadge
                status={channel.status}
                inReview={platformInReview(channel.id)}
              />
            </div>
          ))}
        </div>
        <div className="mt-7 text-center">
          <ArrowLink to="/integrations">Explore all integrations</ArrowLink>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ ai composer */

function AiComposer() {
  return (
    <section className="py-[76px]">
      <div className="mx-auto max-w-[720px] px-6 text-center">
        <div className="flex justify-center">
          <EyebrowLabel icon={Sparkles}>AI COMPOSER</EyebrowLabel>
        </div>
        <h2 className="mt-3.5 font-heading font-normal text-[clamp(32px,4vw,46px)] text-foreground leading-[1.07] tracking-[-0.015em]">
          Never stare at a blank composer again.
        </h2>
        <p className="mt-4 text-[16.5px] text-[oklch(0.46_0.02_264)] leading-relaxed">
          Describe the post you want and PostDaily writes it — on-brand, per
          platform, with the right length, tone and hashtags. Rewrite, shorten
          or spin up a whole thread in one click.
        </p>
        <div className="mx-auto mt-6 flex max-w-[430px] flex-col gap-[13px] text-left">
          {/* These three must name shipped behaviour. The tones are the actual
              `TONE_PRESETS`; the third bullet used to promise "turn one idea
              into a week of posts", which is unbuilt ("fill my week" is parked
              future work), so it now names the workspace-aware layer that does
              ship. */}
          <FeatureCheckItem>
            A brand voice it follows, plus per-message tone
          </FeatureCheckItem>
          <FeatureCheckItem>
            Per-network rewrites, hashtags &amp; emoji
          </FeatureCheckItem>
          <FeatureCheckItem>
            Knows your schedule and your best-performing posts
          </FeatureCheckItem>
        </div>
        <div className="mt-[26px] flex justify-center">
          <ArrowLink to="/features">Explore the composer</ArrowLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- ai agents */

/** Enough names to recognise your app without the row wrapping three times. */
const AGENT_APP_PREVIEW_COUNT = 6;

/**
 * The MCP server, on the home page. Example prompts come from the `/ai-agents`
 * data so the two pages promise the same things; see the rules in
 * `ai-agents-data.ts` before changing what this says.
 */
function AiAgents() {
  return (
    <section className="border-border border-t bg-[oklch(0.975_0.006_264)] py-[80px]">
      <div className="mx-auto grid max-w-[1120px] items-center gap-12 px-6 lg:grid-cols-[1fr_1.05fr]">
        <div>
          <EyebrowLabel icon={Bot}>AI AGENTS · MCP</EyebrowLabel>
          <h2 className="mt-3.5 font-heading font-normal text-[clamp(32px,4vw,46px)] text-foreground leading-[1.07] tracking-[-0.015em]">
            Schedule from any AI agent.
          </h2>
          <p className="mt-4 text-[16.5px] text-[oklch(0.46_0.02_264)] leading-relaxed">
            Connect PostDaily to Claude, ChatGPT, Cursor or any other agent that
            supports MCP. Ask it to write, schedule or check your posts, and it
            works in your queue with each network's real limits.
          </p>
          <div className="mt-6 flex flex-col gap-[13px]">
            <FeatureCheckItem>
              Asks before publishing anything right away
            </FeatureCheckItem>
            <FeatureCheckItem>
              You choose its access and which workspaces it sees
            </FeatureCheckItem>
            <FeatureCheckItem>
              Included on every plan, Free too
            </FeatureCheckItem>
          </div>
          <ul className="mt-6 flex flex-wrap gap-1.5 p-0">
            {clientGuides.slice(0, AGENT_APP_PREVIEW_COUNT).map((guide) => (
              <li
                key={guide.id}
                className="list-none rounded-[7px] border border-border bg-card px-[9px] py-1 text-[12.5px] text-[oklch(0.42_0.02_264)]"
              >
                {guide.name}
              </li>
            ))}
            <li className="list-none px-1 py-1 text-[12.5px] text-muted-foreground">
              and {clientGuides.length - AGENT_APP_PREVIEW_COUNT} more
            </li>
          </ul>
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            <ArrowLink to="/ai-agents">See what it can do</ArrowLink>
            <a
              href={CLAUDE_DIRECTORY_URL}
              rel="noopener"
              target="_blank"
              className="font-medium text-[14.5px] text-[oklch(0.46_0.02_264)] underline-offset-4 hover:underline"
            >
              Listed in Claude's connector directory
            </a>
          </div>
        </div>
        <div className="rounded-[18px] border border-border bg-card p-[22px] shadow-[0_24px_50px_-28px_oklch(0.4_0.08_264/0.3)]">
          <div className="font-semibold text-[12px] text-muted-foreground uppercase tracking-[0.06em]">
            Try asking
          </div>
          <ul className="mt-4 flex flex-col gap-3 p-0">
            {AI_AGENT_EXAMPLES.slice(0, 3).map((example) => (
              <li
                key={example.ask}
                className="list-none rounded-[13px] bg-[oklch(0.97_0.01_264)] p-[13px_15px]"
              >
                <p className="font-medium text-[15px] text-foreground leading-snug">
                  “{example.ask}”
                </p>
                <p className="mt-1.5 text-[13px] text-[oklch(0.5_0.02_264)] leading-[1.5]">
                  {example.does}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ reliability */

const reliabilityChips: { icon: typeof Zap; title: string; sub: string }[] = [
  {
    icon: LineChart,
    title: "Automatic retries",
    sub: "Failed posts self-heal",
  },
  { icon: ShieldCheck, title: "Idempotent queues", sub: "Never double-posts" },
  { icon: Zap, title: "Durable workflows", sub: "Survive any outage" },
  { icon: FileText, title: "Full audit trail", sub: "Every action logged" },
];

function Reliability() {
  return (
    <section className="dark relative overflow-hidden bg-background">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-[120px] left-1/2 h-[500px] w-[900px] -translate-x-1/2 blur-[40px]"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%,oklch(0.55 0.17 268 / 0.35),transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-[1200px] px-6 pt-[82px] pb-[88px]">
        <div className="mx-auto max-w-[660px] text-center">
          <span className="inline-flex h-7 items-center gap-[7px] rounded-full bg-white/[0.08] px-3 font-semibold text-[oklch(0.82_0.08_268)] text-xs tracking-[0.05em]">
            BUILT TO SHIP
          </span>
          <h2 className="mt-[18px] font-heading font-normal text-[clamp(34px,4.6vw,54px)] text-foreground leading-[1.05] tracking-[-0.015em]">
            Posts that actually go out.
          </h2>
          <p className="mt-4 text-[17.5px] text-[oklch(0.74_0.02_264)] leading-relaxed">
            Behind the calm interface is a serious publishing engine —
            idempotent queues, durable workflows and automatic retries. Track
            every post, measure what works, and keep a full, searchable audit
            trail.
          </p>
        </div>

        <div className="mx-auto mt-12 flex max-w-[940px] flex-wrap gap-3">
          {reliabilityChips.map((chip) => (
            <div
              key={chip.title}
              className="flex flex-[1_1_200px] items-center gap-[11px] rounded-[13px] border border-white/[0.07] bg-white/[0.04] p-[14px_16px]"
            >
              <span className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[9px] bg-[oklch(0.55_0.15_264/0.25)] text-[oklch(0.82_0.1_268)]">
                <chip.icon className="size-[17px]" strokeWidth={2} />
              </span>
              <div>
                <div className="font-semibold text-[13.5px] text-foreground">
                  {chip.title}
                </div>
                <div className="text-[11.5px] text-muted-foreground">
                  {chip.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- assembly */

/**
 * Landing page order is a funnel: show the product first (the showcase does the
 * work a demo call used to), then where it publishes, then how writing feels,
 * then that it works from the AI app they already use, then how to start, then why it can be trusted, then what it costs, then the
 * objections — and only then ask for the signup.
 */
export function LandingPage() {
  return (
    <div className="pd-marketing min-h-screen overflow-x-clip font-sans text-foreground antialiased">
      <MarketingStyles />
      <AnnouncementBar />
      <SiteHeader active="home" />
      <main>
        <LandingHero />
        {/*
         * Everything from here down is server-rendered HTML that nobody can see
         * until they scroll. Deferring its hydration keeps it out of the first
         * paint's way: the compiler moves each boundary's children into their
         * own chunk, so the browser stops downloading — and the main thread
         * stops executing — the whole page's JavaScript before the hero has
         * even painted. The markup itself is unchanged, so this is invisible to
         * a crawler and to anyone reading rather than clicking.
         */}
        <Hydrate when={onApproach}>
          <LandingShowcase />
          <ChannelShowcase />
          <AiComposer />
          <AiAgents />
          <LandingSteps />
          <Reliability />
        </Hydrate>
        {/*
         * The pricing block is its own boundary because it is the one that
         * fetches: hydrating it is what fires `billing.listPlans`, and that
         * request has no business competing with the hero.
         */}
        <Hydrate when={onApproach}>
          <LandingPricing />
        </Hydrate>
        <Hydrate when={onApproach}>
          <RelatedLinks
            heading="Guides and free tools"
            links={HOME_GUIDE_LINKS}
          />
        </Hydrate>
        <Hydrate when={onApproach}>
          <LandingFaq />
          <MarketingCta />
        </Hydrate>
      </main>
      <Hydrate when={onApproach}>
        <SiteFooter />
      </Hydrate>
    </div>
  );
}
